import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { DatePipe } from '@angular/common';
import * as XLSX from 'xlsx';
import { MatSnackBar } from '@angular/material';

export interface ReporteDeuda {
  codconc: string;
  descripcion_concepto: string;
  importe: number;
  inddbcr: string;
  fecproc: string;
  fecrec: string;
  cuitcont: string;
  periodo: string;
  cuitapo: string;
  deuda_nomina: number;
  tiene_pago: number;
}

@Component({
  selector: 'app-reporte-deudas',
  templateUrl: './reporte-deudas.component.html',
  styleUrls: ['./reporte-deudas.component.css']
})

export class ReporteDeudasComponent implements OnInit {

  // Filtros
  filtroCuit;
  filtroPeriodoDesde;
  filtroPeriodoHasta;
  filtroEstadoPago; // 'todos', 'pagados', 'impagos'
  filtroCodconc: string = '';

  // Estado
  loading;
  loadingExcel = false;

  // Tabla
  // tslint:disable-next-line: max-line-length
  displayedColumns: string[] = ['cuitcont', 'periodo', 'codconc', 'descripcion_concepto', 'inddbcr', 'importe', 'fecproc', 'fecrec', 'cuitapo', 'deuda_nomina', 'tiene_pago'];
  dataSource;
  dataSourceCompleto; // Guarda TODOS los datos sin filtrar

  // Paginación
  pageSize = 1000;
  pageIndex = 0;
  totalRecords = 0;
  pageSizeOptions = [100, 500, 1000, 2000];

  fecha_hoy;

  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
    public snackBar: MatSnackBar) {
      this.loading = false;
      this.fecha_hoy = new Date;
      this.fecha_hoy = this.datePipe.transform(this.fecha_hoy, 'yyyy-MM-dd');
      this.filtroCuit = '';
      this.filtroPeriodoDesde = '20/01';
      this.filtroPeriodoHasta = '25/10';
      this.filtroEstadoPago = 'todos';
      this.dataSource = null;
      this.dataSourceCompleto = null;
  }

  ngOnInit() {
    console.log('Reporte de Deudas iniciado');
  }

  /**
   * Buscar deudas según filtros (resetea paginación y ejecuta búsqueda)
   */
  buscarDeudas() {
    if (!this.filtroPeriodoDesde || !this.filtroPeriodoHasta) {
      this.snackBar.open('Debe seleccionar un rango de períodos', 'Cerrar', {
        duration: 3000,
      });
      return;
    }

    // Resetear a la primera página cuando se hace una búsqueda nueva
    this.pageIndex = 0;

    // Ejecutar la consulta correspondiente
    this.cargarPagina();
  }

  /**
   * Cargar página actual (sin resetear paginación)
   */
  private cargarPagina() {
    if (this.filtroEstadoPago === 'impagos') {
      this.cargarPaginaImpagos();
      return;
    }
    if (this.filtroEstadoPago === 'todos') {
      this.cargarPaginaTodos();
      return;
    }
    this.cargarPaginaNormal();
  }

  /**
   * Cargar página de consulta normal de deudas
   */
  private cargarPaginaNormal() {

    this.loading = true;
    this.dataSource = null;

    // Construir URL con parámetros incluyendo paginación
    // http://localhost:1337/api/reporte-deudas/consultar?cuit=30-54634701-6&perdes=15/01&perhas=25/12&page=1&pageSize=1000
    const page = this.pageIndex + 1; // Backend usa páginas 1-indexed
    let url = `${environment.backendUrl}/api/reporte-deudas/consultar?perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}&page=${page}&pageSize=${this.pageSize}`;

    if (this.filtroCuit && this.filtroCuit.trim() !== '') {
      url += `&cuit=${this.filtroCuit}`;
    }

    console.log('\x1b[36m%s\x1b[0m', `Consultando: ${url}`);

    this.http.get(url).subscribe(
      (response: any) => {
        if (response && response.data) {
          // Guardar datos
          this.dataSourceCompleto = response.data;
          this.dataSource = response.data;

          this.totalRecords = response.total || 0;
          console.log(`Página ${response.page}: ${response.data.length} registros (total: ${response.total})`);

          this.snackBar.open(`Se encontraron ${response.total} registros`, 'Cerrar', {
            duration: 3000,
          });
        } else {
          console.warn('NO HAY DATOS');
          this.dataSource = [];
          this.dataSourceCompleto = [];
          this.totalRecords = 0;
          this.snackBar.open('No se encontraron resultados', 'Cerrar', {
            duration: 3000,
          });
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error:', error);
        this.snackBar.open('Error al consultar el servidor', 'Cerrar', {
          duration: 5000,
        });
        this.loading = false;
      }
    );
  }

  /**
   * Cargar página combinando pagados + impagos (opcion TODOS, requiere CUIT)
   */
  private cargarPaginaTodos() {
    if (!this.filtroCuit || this.filtroCuit.trim() === '') {
      this.snackBar.open('Debe ingresar un CUIT para consultar TODOS los estados', 'Cerrar', { duration: 4000 });
      return;
    }

    this.loading = true;
    this.dataSource = null;

    const page = this.pageIndex + 1;
    const url = `${environment.backendUrl}/api/reporte-deudas/todos?cuit=${this.filtroCuit}&perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}&page=${page}&pageSize=${this.pageSize}`;

    console.log('\x1b[36m%s\x1b[0m', `Consultando todos: ${url}`);

    this.http.get(url).subscribe(
      (response: any) => {
        if (response && response.data) {
          this.dataSourceCompleto = response.data;
          this.dataSource = response.data;
          this.totalRecords = response.total || 0;
          this.snackBar.open(`Se encontraron ${response.total} registros`, 'Cerrar', { duration: 3000 });
        } else {
          this.dataSource = [];
          this.dataSourceCompleto = [];
          this.totalRecords = 0;
          this.snackBar.open('No se encontraron resultados', 'Cerrar', { duration: 3000 });
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error:', error);
        this.snackBar.open('Error al consultar el servidor', 'Cerrar', { duration: 5000 });
        this.loading = false;
      }
    );
  }

  /**
   * Cargar página de consulta de períodos impagos
   * Ahora soporta consulta sin CUIT (trae impagos de todos los CUITs)
   */
  private cargarPaginaImpagos() {
    this.loading = true;
    this.dataSource = null;

    // Construir URL con paginación
    const page = this.pageIndex + 1;
    let url = `${environment.backendUrl}/api/reporte-deudas/periodos-impagos?perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}&page=${page}&pageSize=${this.pageSize}`;

    // Solo agregar CUIT si tiene valor
    if (this.filtroCuit && this.filtroCuit.trim() !== '') {
      url += `&cuit=${this.filtroCuit}`;
    }

    console.log('\x1b[36m%s\x1b[0m', `Consultando impagos: ${url}`);

    // Mostrar advertencia si no hay CUIT (consulta puede ser lenta)
    if (!this.filtroCuit || this.filtroCuit.trim() === '') {
      this.snackBar.open('Consultando impagos de todos los CUITs. Esto puede tardar 30-60 segundos...', 'Cerrar', {
        duration: 5000,
      });
    }

    this.http.get(url).subscribe(
      (response: any) => {
        if (response && response.data && response.data.length > 0) {
          // El endpoint retorna: { data, total, page, pageSize }
          const datos = response.data;

          // Adaptar estructura para mostrar en la tabla
          this.dataSource = datos.map(item => ({
            codconc: '',
            descripcion_concepto: 'PERÍODO IMPAGO',
            importe: 0,
            inddbcr: '',
            fecproc: null,
            fecrec: null,
            cuitcont: item.cuitcont,  // Ahora viene del backend
            periodo: item.periodo,
            cuitapo: '',
            deuda_nomina: item.deuda_nomina,
            tiene_pago: 0,
            cant_empleados: item.cant_empleados
          }));

          this.dataSourceCompleto = this.dataSource;
          this.totalRecords = response.total || datos.length;

          console.log(`Períodos impagos encontrados: ${datos.length} de ${response.total} totales (página ${page})`);
          this.snackBar.open(`Se encontraron ${response.total} períodos impagos`, 'Cerrar', {
            duration: 3000,
          });
        } else {
          console.warn('NO HAY PERÍODOS IMPAGOS');
          this.dataSource = [];
          this.dataSourceCompleto = [];
          this.totalRecords = 0;
          this.snackBar.open('No se encontraron períodos impagos', 'Cerrar', {
            duration: 3000,
          });
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error:', error);
        this.snackBar.open('Error al consultar períodos impagos', 'Cerrar', {
          duration: 5000,
        });
        this.loading = false;
      }
    );
  }


  /**
   * Manejar cambio de página en el paginator
   */
  onPageChange(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    // Solo cargar la página, NO resetear la búsqueda
    this.cargarPagina();
  }

  /**
   * Ir a una página específica
   */
  goToPage(page: number) {
    this.pageIndex = page;
    this.cargarPagina();
  }

  /**
   * Cambiar tamaño de página
   */
  changePageSize(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 0; // Volver a la primera página
    this.cargarPagina();
  }

  /**
   * Obtener números de página a mostrar en el paginador
   * Muestra hasta 7 números con elipsis cuando hay muchas páginas
   */
  getPageNumbers(): (number | string)[] {
    const totalPages = Math.ceil(this.totalRecords / this.pageSize);
    const currentPage = this.pageIndex;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      // Mostrar todas las páginas
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Mostrar páginas con elipsis
      if (currentPage < 4) {
        // Inicio: 0 1 2 3 4 ... last
        for (let i = 0; i < 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 4) {
        // Final: 0 ... last-4 last-3 last-2 last-1 last
        pages.push(0);
        pages.push('...');
        for (let i = totalPages - 5; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Medio: 0 ... current-1 current current+1 ... last
        pages.push(0);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages - 1);
      }
    }

    return pages;
  }

  /**
   * Obtener número total de páginas
   */
  getTotalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }


  /**
   * Exportar a Excel
   */
  exportarExcel() {
    if (!this.totalRecords || this.totalRecords === 0) {
      this.snackBar.open('No hay datos para exportar', 'Cerrar', { duration: 3000 });
      return;
    }

    this.loadingExcel = true;
    this.snackBar.open('Preparando exportación de todos los registros...', '', { duration: 5000 });

    const esImpagos = this.filtroEstadoPago === 'impagos';
    const esTodos = this.filtroEstadoPago === 'todos';
    let url: string;

    if (esImpagos) {
      url = `${environment.backendUrl}/api/reporte-deudas/periodos-impagos?perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}&page=1&pageSize=${this.totalRecords}`;
      if (this.filtroCuit && this.filtroCuit.trim() !== '') {
        url += `&cuit=${this.filtroCuit}`;
      }
    } else if (esTodos) {
      url = `${environment.backendUrl}/api/reporte-deudas/todos?cuit=${this.filtroCuit}&perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}&page=1&pageSize=${this.totalRecords}`;
    } else {
      url = `${environment.backendUrl}/api/reporte-deudas/consultar?perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}&page=1&pageSize=${this.totalRecords}`;
      if (this.filtroCuit && this.filtroCuit.trim() !== '') {
        url += `&cuit=${this.filtroCuit}`;
      }
    }

    this.http.get(url).subscribe(
      (response: any) => {
        this.loadingExcel = false;

        if (!response || !response.data || response.data.length === 0) {
          this.snackBar.open('No hay datos para exportar', 'Cerrar', { duration: 3000 });
          return;
        }

        let datos = response.data;

        if (esImpagos) {
          datos = datos.map(item => ({
            cuitcont: item.cuitcont,
            periodo: item.periodo,
            codconc: '',
            descripcion_concepto: 'PERÍODO IMPAGO',
            inddbcr: '',
            importe: 0,
            fecproc: null,
            fecrec: null,
            cuitapo: '',
            deuda_nomina: item.deuda_nomina,
            tiene_pago: 0
          }));
        }

        // Aplicar filtro de concepto si está activo
        if (this.filtroCodconc) {
          datos = datos.filter(row => String(row.codconc) === String(this.filtroCodconc));
        }

        const dataParaExcel = datos.map(row => ({
          'CUIT Contribuyente': row.cuitcont,
          'Período': row.periodo,
          'Código Concepto': row.codconc,
          'Descripción': row.descripcion_concepto,
          'Débito/Crédito': row.inddbcr,
          'Importe': row.importe,
          'Fecha Proceso': row.fecproc,
          'Fecha Recaudación': row.fecrec,
          'CUIT Aportante': row.cuitapo,
          'Remuneración Imponible': row.deuda_nomina,
          'Tiene Pago': row.tiene_pago === 1 ? 'SÍ' : 'NO'
        }));

        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataParaExcel);
        const workbook: XLSX.WorkBook = {
          Sheets: { 'Reporte Deudas': worksheet },
          SheetNames: ['Reporte Deudas']
        };

        const nombreArchivo = `Reporte_Deudas_${this.filtroPeriodoDesde}_${this.filtroPeriodoHasta}_${this.fecha_hoy}.xlsx`;
        XLSX.writeFile(workbook, nombreArchivo, { bookType: 'xlsx', type: 'buffer' });

        this.snackBar.open(`Excel exportado: ${dataParaExcel.length} registros`, 'Cerrar', { duration: 3000 });
      },
      (error) => {
        this.loadingExcel = false;
        console.error('Error al exportar:', error);
        this.snackBar.open('Error al exportar a Excel', 'Cerrar', { duration: 5000 });
      }
    );
  }


  /**
   * Limpiar filtros
   */
  limpiarFiltros() {
    this.filtroCuit = '';
    this.filtroPeriodoDesde = '20/01';
    this.filtroPeriodoHasta = '25/10';
    this.filtroEstadoPago = 'todos';
    this.filtroCodconc = '';
    this.dataSource = null;
    this.dataSourceCompleto = null;
    this.pageIndex = 0;
    this.totalRecords = 0;
  }

  getCodconcOptions(): { codconc: string, descripcion: string }[] {
    if (!this.dataSourceCompleto) return [];
    const seen = new Set<string>();
    const options: { codconc: string, descripcion: string }[] = [];
    for (const row of this.dataSourceCompleto) {
      if (row.codconc && !seen.has(row.codconc)) {
        seen.add(row.codconc);
        options.push({ codconc: row.codconc, descripcion: row.descripcion_concepto || '' });
      }
    }
    return options.sort((a, b) => a.codconc.localeCompare(b.codconc));
  }

  aplicarFiltroCodconc() {
    if (!this.dataSourceCompleto) return;
    if (!this.filtroCodconc) {
      this.dataSource = this.dataSourceCompleto;
    } else {
      this.dataSource = this.dataSourceCompleto.filter(
        row => row.codconc === this.filtroCodconc
      );
    }
  }
}
